import { inverseChainData, inverseConfigFor } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { inverseLenderKey } from '../../public-data/inverse/convertPublic'
import { INVERSE_CALLS_PER_MARKET, INVERSE_TAIL_CALLS } from './userCallBuild'
import { rebuildInverseData, usesInverseLens } from './lens'

/** Per-position FiRM detail attached to the debt row (raw strings). */
export interface InversePositionInfo {
  /** Pessimistic-oracle borrow ceiling for the CURRENT collateral (DOLA raw). */
  creditLimit: string
  /** Max collateral withdrawable right now (collateral raw). */
  withdrawalLimit: string
  /** DBR wallet balance (raw) — the prepaid-interest runway. */
  dbrBalance: string
  /** DBR deficit (raw). > 0 ⇒ force-replenishable at 54.75% APR AND
   *  withdrawals are FROZEN until the user buys DBR. */
  dbrDeficit: string
  /** DBR signed balance (raw, may be negative). */
  dbrSignedBalance: string
  /** `DBR.debts(user)` (raw DOLA) — debt across ALL FiRM markets, which is
   *  what burns DBR at 1 per DOLA-year. Chain-wide, repeated on every row. */
  dbrTotalDebt: string
  /** Seconds until `dbrBalance` is exhausted at that burn ('0' if no debt). */
  dbrRunwaySeconds: string
  /** Unix seconds of the projected depletion — past that point anyone can
   *  force-replenish the account, charging the replenished DBR to its DOLA
   *  debt at `replenishmentPriceBps`, and withdrawals freeze. Absent when
   *  there is no debt. */
  dbrDepletionTimestamp?: string
}

const big = (v: any): bigint => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number' || typeof v === 'string') return BigInt(v)
  } catch {}
  return 0n
}

/**
 * Returns a [converter, expectedCallCount] tuple for FiRM user data.
 *
 * Layout mirrors `buildInverseUserCall` (deterministic from config — no
 * discovery). Emits one `UserData` per market key
 * `INVERSE_<MARKET_ADDR>`, each with a single sub-account `'0'`
 * (escrows are address-keyed: at most ONE position per user per
 * market): collateral row (escrow balance; a never-deployed escrow
 * parses as 0) + DOLA debt row (STATIC principal — interest burns DBR,
 * it never accrues here) with `inverseInfo` (credit/withdrawal limits +
 * the chain-wide DBR state repeated per market so any single row is
 * self-sufficient for the frontend's DBR prompt).
 */
export const getInverseUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  const cfg = inverseConfigFor(lender, chainId)
  const markets = inverseChainData(lender, chainId)?.markets ?? []
  const viaLens = usesInverseLens(chainId)
  /** Slots the flat walk array carries — what every index below is written against. */
  const walkSlots =
    cfg && markets.length > 0
      ? markets.length * INVERSE_CALLS_PER_MARKET + INVERSE_TAIL_CALLS
      : 0
  /** CALLS the framework hands us: one blob on the lens path, the walk otherwise. */
  const expected = walkSlots === 0 ? 0 : viaLens ? 1 : walkSlots

  return [
    (raw: any[]) => {
      if (!cfg || expected === 0) return undefined
      if (!raw || raw.length < expected) return undefined

      // One blob in, the walk's own flat array out — every index below unchanged.
      const data = viaLens ? rebuildInverseData(raw[0], markets.length) : raw
      if (!data || data.length < walkSlots) return undefined

      const dolaAddr = cfg.dola.toLowerCase()
      const debtDecimals = cfg.debtDecimals ?? 18
      const tailBase = markets.length * INVERSE_CALLS_PER_MARKET
      const dbrBalance = big(data[tailBase])
      const dbrDeficit = big(data[tailBase + 1])
      const dbrSignedRaw = data[tailBase + 2]
      const dbrSigned = isFailedCall(dbrSignedRaw) ? 0n : big(dbrSignedRaw)
      // Chain-wide DOLA debt = the DBR burn base (1 DBR per DOLA-year).
      const dbrTotalDebtRaw = data[tailBase + 3]
      const dbrTotalDebt = isFailedCall(dbrTotalDebtRaw)
        ? 0n
        : big(dbrTotalDebtRaw)
      // Seconds until the balance is exhausted and the account becomes
      // force-replenishable; 0 debt ⇒ nothing burns, so no depletion.
      const dbrRunway =
        dbrTotalDebt > 0n ? (dbrBalance * 31_536_000n) / dbrTotalDebt : 0n
      const nowSec = BigInt(Math.floor(Date.now() / 1000))

      const out: { [lender: string]: UserData } = {}

      markets.forEach((market, i) => {
        const base = i * INVERSE_CALLS_PER_MARKET
        // A failed escrow read is EXPECTED for a user with no position
        // here (the escrow was never deployed → no code → failed call).
        const escrowBal = isFailedCall(data[base]) ? 0n : big(data[base])
        const debtRaw = data[base + 1]
        // A failed debts() read is not an empty position — skip the
        // market rather than reporting the user as debt-free.
        if (isFailedCall(debtRaw)) return
        const debt = big(debtRaw)
        if (escrowBal === 0n && debt === 0n) return
        const creditLimit = isFailedCall(data[base + 2])
          ? 0n
          : big(data[base + 2])
        const withdrawalLimit = isFailedCall(data[base + 3])
          ? 0n
          : big(data[base + 3])

        const lenderKey = inverseLenderKey(lender, market.address)
        const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
        if (!metaMap) return

        const collAddr = market.collToken.toLowerCase()
        const collUid = createMarketUid(chainId, lenderKey, collAddr)
        const loanUid = createMarketUid(chainId, lenderKey, dolaAddr)
        const collMeta = metaMap[collUid]
        const loanMeta = metaMap[loanUid]
        const collDecimals = collMeta?.asset?.decimals ?? market.collDecimals
        const collDisplay = collMeta ? getDisplayPrice(collMeta) : 0
        const collOracle = collMeta ? getOraclePrice(collMeta) : 0
        const collHist = collMeta?.price?.priceUsd24h ?? collDisplay
        const loanDisplay = loanMeta ? getDisplayPrice(loanMeta) : 0
        const loanOracle = loanMeta ? getOraclePrice(loanMeta) : 0
        const loanHist = loanMeta?.price?.priceUsd24h ?? loanDisplay

        const inverseInfo: InversePositionInfo = {
          creditLimit: creditLimit.toString(),
          withdrawalLimit: withdrawalLimit.toString(),
          dbrBalance: dbrBalance.toString(),
          dbrDeficit: dbrDeficit.toString(),
          dbrSignedBalance: dbrSigned.toString(),
          dbrTotalDebt: dbrTotalDebt.toString(),
          dbrRunwaySeconds: dbrRunway.toString(),
          dbrDepletionTimestamp:
            dbrTotalDebt > 0n ? String(nowSec + dbrRunway) : undefined,
        }

        const collStr = parseRawAmount(escrowBal.toString(), collDecimals)
        const collNum = Number(collStr)
        const debtStr = parseRawAmount(debt.toString(), debtDecimals)
        const debtNum = Number(debtStr)

        const lendingPositions: Record<string, any> = {
          '0': {
            [collUid]: {
              marketUid: collUid,
              underlying: collAddr,
              deposits: collStr,
              debt: '0',
              debtStable: '0',
              depositsUSD: collNum * collDisplay,
              debtUSD: 0,
              debtStableUSD: 0,
              depositsUSDOracle: collNum * collOracle,
              debtUSDOracle: 0,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: true,
              claimableRewards: 0,
            },
            [loanUid]: {
              marketUid: loanUid,
              underlying: dolaAddr,
              deposits: '0',
              debt: debtStr,
              debtStable: '0',
              depositsUSD: 0,
              debtUSD: debtNum * loanDisplay,
              debtStableUSD: 0,
              depositsUSDOracle: 0,
              debtUSDOracle: debtNum * loanOracle,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: false,
              claimableRewards: 0,
              inverseInfo,
            },
          },
        }
        const modes: Record<string, number> = { '0': 0 }
        const hist = {
          '0': {
            totalDeposits24h: collNum * collHist,
            totalDebt24h: debtNum * loanHist,
          },
        }

        const userData = createMultiAccountTypeUserState(
          { chainId, account, lendingPositions, modes } as any,
          metaMap,
          hist,
        )
        if (userData && userData.data.length > 0) out[lenderKey] = userData
      })

      return Object.keys(out).length > 0 ? out : undefined
    },
    expected,
  ]
}
